// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

export type LogLevel = 'info' | 'good' | 'warn' | 'error';

const MAX_EVENTS = 600;

interface Entry {
  readonly at: Date;
  readonly level: LogLevel;
  readonly message: string;
}

/**
 * The event drawer.
 *
 * It exists to build trust: the user watches the tool describe what it is doing, including every
 * decision it made about their images.
 *
 * Filenames are the only user data that ever reaches it, and they are written with `textContent`
 * — never `innerHTML` — because a filename inside a dropped ZIP is attacker-controllable.
 */
export class EventLog {
  private readonly entries: Entry[] = [];
  private readonly list: HTMLOListElement;

  constructor(list: HTMLOListElement) {
    this.list = list;
  }

  emit(level: LogLevel, message: string): void {
    const entry = { at: new Date(), level, message };
    this.entries.push(entry);
    if (this.entries.length > MAX_EVENTS) this.entries.shift();

    const li = document.createElement('li');
    const t = document.createElement('span');
    t.className = 't';
    t.textContent = `[${entry.at.toTimeString().slice(0, 8)}] `;
    const m = document.createElement('span');
    m.className = `lv-${level}`;
    // textContent, never innerHTML: log lines quote filenames, which are user-controlled.
    m.textContent = message;
    li.append(t, m);
    this.list.appendChild(li);
    while (this.list.children.length > MAX_EVENTS) this.list.firstElementChild?.remove();
    this.list.scrollTop = this.list.scrollHeight;
  }

  asText(): string {
    return this.entries
      .map((e) => `[${e.at.toISOString()}] ${e.level.toUpperCase()} ${e.message}`)
      .join('\n');
  }
}
